import {
  changePassword,
  confirmEmail,
  disable2fa,
  enable2fa,
  generate2fa,
  login,
  logout,
  register,
  resetPassword,
  updateLoggedPassword,
  verify2fa
} from '@/api/auth'
import { extractData, queryClient } from '@/lib/vue-query'
import { useAuthStore } from '@/stores/authStore'
import { Generate2FAResponseSchema, type LoginRequest } from '@/types/api'
import { ErrorCodes } from '@/lib/error-handler'
import { useAuthFlow } from '@/composables/useAuthFlow'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'
import { QUERY_CONFIGS, createSmartRetry, createQueryKey } from '@/lib/query-helpers'
import { isAuthError } from '@/lib/axios'

/**
 * Authentication queries
 */

const authRetry = createSmartRetry(1, [2000, 7003, 7004, 10000, 5000, 5001, 5002, 5007, 10001])

/**
 * Login mutation
 */
export const useLoginMutation = () => {
  const { handle2FARequired, handleCaptchaRequired, completeLogin, is2FARequiredError, isCaptchaRequiredError, is2FARequiredData } = useAuthFlow()

  return useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      const response = await login(credentials)

      if (response.data && response.data.code === 7004) {
        const error = {
          code: 7004,
          message: response.data.message || '2FA token is required',
          credentials
        }
        throw error
      }

      if (response.data && response.data.code === ErrorCodes.CAPTCHA_REQUIRED) {
        const error = {
          code: ErrorCodes.CAPTCHA_REQUIRED,
          message: response.data.message || 'CAPTCHA verification required',
          credentials
        }
        throw error
      }

      return extractData(response, 'LoginMutation', 'authenticate')
    },
    onSuccess: async (data: any, variables: LoginRequest) => {
      if (is2FARequiredData(data)) {
        handle2FARequired(variables)
        return
      }
      await completeLogin()
    },
    onError: (error: any) => {
      if (isAuthError(error)) return

      if (is2FARequiredError(error)) {
        const credentials = error.credentials || {}
        if (credentials.username && credentials.password) {
          handle2FARequired(credentials)
          return
        }
      }

      if (isCaptchaRequiredError(error)) {
        const credentials = error.credentials || {}
        if (credentials.username && credentials.password) {
          handleCaptchaRequired(credentials)
          return
        }
      }
    }
  })
}

/**
 * Logout mutation
 */
export const useLogoutMutation = () => {
  const { performLogout } = useAuthFlow()

  return useMutation({
    mutationFn: async () => {
      const response = await logout()
      return extractData(response, "AuthMutation", "authAction")
    },
    onSuccess: performLogout,
    onError: performLogout
  })
}

/**
 * Verify 2FA mutation
 */
export const useVerify2FAMutation = () => {
  const { complete2FALogin, handleCaptchaRequired, isCaptchaRequiredError } = useAuthFlow()

  return useMutation({
    mutationFn: async (params: { username: string; password: string; token_2fa: string; captcha_token?: string }) => {
      const response = await verify2fa(params)

      if (response.data && response.data.code === ErrorCodes.CAPTCHA_REQUIRED) {
        const error = {
          code: ErrorCodes.CAPTCHA_REQUIRED,
          message: response.data.message || 'CAPTCHA verification required',
          credentials: params
        }
        throw error
      }

      return extractData(response, 'Verify2FAMutation', 'verify2fa')
    },
    onSuccess: complete2FALogin,
    onError: (error: any) => {
      if (isCaptchaRequiredError(error)) {
        const credentials = error.credentials || {}
        if (credentials.username && credentials.password) {
          handleCaptchaRequired(credentials)
          return
        }
      }
    }
  })
}

/**
 * Registration mutation
 */
export const useRegisterMutation = () => {
  return useMutation({
    mutationFn: async (params: {
      user_name: string
      email: string
      password: string
      confirmation_code: number
    }) => {
      const response = await register(params)
      return extractData(response, 'RegisterMutation', 'register')
    },
    retry: authRetry
  })
}

/**
 * Password reset mutation
 */
export const useResetPasswordMutation = () => {
  return useMutation({
    mutationFn: async (email: string) => {
      const response = await resetPassword(email)
      return extractData(response, "AuthMutation", "authAction")
    },
    retry: authRetry
  })
}

/**
 * Change password mutation
 */
export const useChangePasswordMutation = () => {
  return useMutation({
    mutationFn: async (params: { user_id: number; token: string; password: string }) => {
      const response = await changePassword(params)
      return extractData(response, "AuthMutation", "authAction")
    },
    retry: authRetry
  })
}

/**
 * Email confirmation mutation
 */
export const useConfirmEmailMutation = () => {
  return useMutation({
    mutationFn: async (email: string) => {
      const response = await confirmEmail(email)
      return extractData(response, "AuthMutation", "authAction")
    },
    retry: authRetry
  })
}

/**
 * Generate 2FA query
 */
export const useGenerate2FAQuery = (enabled = true) => {
  const authStore = useAuthStore()

  return useQuery({
    queryKey: createQueryKey.auth.generate2fa(),
    queryFn: async () => {
      const response = await generate2fa()
      const data = extractData(response, "GetUserInfoQuery", "getUserInfo")
      return Generate2FAResponseSchema.parse(data)
    },
    enabled: enabled && computed(() => authStore.isAuthenticated),
    ...QUERY_CONFIGS.STABLE,
    retry: authRetry
  })
}

/**
 * Enable 2FA mutation
 */
export const useEnable2FAMutation = () => {
  return useMutation({
    mutationFn: async (params: { password: string; token_email: number; token_2fa: string }) => {
      const response = await enable2fa(params)
      return extractData(response, "AuthMutation", "authAction")
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: createQueryKey.user.info() })
    },
    retry: authRetry
  })
}

/**
 * Disable 2FA mutation
 */
export const useDisable2FAMutation = () => {
  return useMutation({
    mutationFn: async (token: string) => {
      const response = await disable2fa(token)
      return extractData(response, "AuthMutation", "authAction")
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: createQueryKey.user.info() })
    },
    retry: authRetry
  })
}

/**
 * Send email confirmation mutation
 */
export const useSendEmailConfirmationMutation = () => {
  return useMutation({
    mutationFn: async (email: string) => {
      const response = await confirmEmail(email)
      return extractData(response, "AuthMutation", "authAction")
    },
    retry: authRetry
  })
}

/**
 * Update logged user password mutation
 */
export const useUpdateLoggedPasswordMutation = () => {
  return useMutation({
    mutationFn: async (params: { old_password: string; new_password: string }) => {
      const response = await updateLoggedPassword(params)
      return extractData(response, "AuthMutation", "authAction")
    },
    retry: authRetry
  })
}
